<template>
  <div class="header-carousel-vue">
    <div class="header-carousel-item" v-for="(slide, index) in slides" :key="index"
      :class="{ 'active': index === currentSlide }" :style="{ 'z-index': index === currentSlide ? 1 : 0 }">
      <img :src="slide.image" class="img-fluid w-100" alt="Image" loading="lazy" />
      <div class="carousel-caption">
        <div class="container">
          <div class="row gy-0 gx-5">
            <div class="col-lg-0 col-xl-5"></div>
            <div class="col-xl-7 animated fadeInLeft">
              <div class="text-sm-center text-md-end">
                <h4 class="text-primary text-uppercase fw-bold mb-4">
                  {{ slide.title }}
                </h4>
                <p class="mb-5 fs-5 text-capitalize">
                  {{ slide.subtitle }}
                </p>
                <div class="d-flex align-items-center justify-content-center justify-content-md-end">
                  <div class="d-flex justify-content-end ms-2">
                    <a class="btn btn-md-square btn-light rounded-circle me-2" href=""><i
                        class="fab fa-facebook-f"></i></a>
                    <a class="btn btn-md-square btn-light rounded-circle mx-2" href=""><i
                        class="fab fa-twitter"></i></a>
                    <a class="btn btn-md-square btn-light rounded-circle mx-2" href=""><i
                        class="fab fa-instagram"></i></a>
                    <a class="btn btn-md-square btn-light rounded-circle ms-2" href=""><i
                        class="fab fa-linkedin-in"></i></a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HeaderCarousel',
  data() {
    return {
      slides: [],
      currentSlide: 0, // This is now CRITICAL for controlling visibility
      interval: null,
      // ... (Your titles and subtitles arrays remain the same)
      titles: [
        "The Global Call: Crisis & Opportunity",
        "Food Security and Sustainable Living",
        "Education is the Foundation of Hope",
      ],
      subtitles: [
        "Mobilizing resources to provide immediate relief and long-term recovery in vulnerable regions.",
        "Working with local farmers to implement climate-resilient agriculture and feed the hungry.",
        "Breaking the cycle of poverty by providing access to quality schooling for every child.",
      ]
    };
  },
  methods: {
    // Your loadImages method (using API fetch) is correct
    async loadImages(folder) {
      try {
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}api/images/${folder}`);
        return await response.json(); // should return array of image URLs
      } catch (err) {
        console.error("Failed to load images", err);
        return [];
      }
    },
    // ... (Your getRandomItem, randomizeImages, generateSlides methods remain the same)
    getRandomItem(arr) {
      return arr[Math.floor(Math.random() * arr.length)];
    },
    randomizeImages(images) {
      const shuffled = images.slice();
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    },
    generateSlides(images) {
      const titlesCopy = [...this.titles];
      const subtitlesCopy = [...this.subtitles];

      return images.map((img) => {
        const titleIndex = Math.floor(Math.random() * titlesCopy.length);
        const subtitleIndex = Math.floor(Math.random() * subtitlesCopy.length);

        const title = titlesCopy.splice(titleIndex, 1)[0] || this.getRandomItem(this.titles);
        const subtitle = subtitlesCopy.splice(subtitleIndex, 1)[0] || this.getRandomItem(this.subtitles);

        return { image: img, title, subtitle };
      });
    },
    // Your startAutoPlay method is now the core transition engine
    startAutoPlay() {
      this.stopAutoPlay(); // Clear any existing interval just in case
      if (this.slides.length > 1) {
        this.interval = setInterval(() => {
          this.currentSlide = (this.currentSlide + 1) % this.slides.length;
        }, 5000);
      }
    },
    stopAutoPlay() {
      clearInterval(this.interval);
    }
  },
  // Ensure mounted is async to wait for the API call
  async mounted() {
    const images = await this.loadImages("NGO"); // ✅ Await backend response
    const randomized = this.randomizeImages(images);
    this.slides = this.generateSlides(randomized);

    if (this.slides.length > 0) {
      this.startAutoPlay(); // Start Vue's manual autoplay
    }
  },
  beforeUnmount() {
    this.stopAutoPlay();
  }
};
</script>

<style scoped>
/* --- CORE CAROUSEL STRUCTURE --- */
.header-carousel-vue {
  position: relative;
  overflow: hidden;
  height: 95vh;
  /* Adjust as needed */
  width: 100%;
}

.header-carousel-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  visibility: hidden;
  /* Base transition for opacity and visibility */
  transition: opacity 1s ease-in-out, visibility 1s ease-in-out;
  z-index: 0;
}

.header-carousel-item.active {
  opacity: 1;
  visibility: visible;
  z-index: 10;
}

/* --- IMAGE ANIMATION (Zoom/Bouncing Effect) --- */
.header-carousel-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;      /* fill the container without distortion */
  object-position: center;
  
  /* Animation/zoom effect */
  transform: scale(1.05);
  transition: transform 6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.header-carousel-item.active img {
  /* When active, the image slowly zooms in to normal size */
  transform: scale(1);
}

/* --- CAPTION ANIMATION (Bouncing Text Effect) --- */

/* 1. Ensure caption elements start off-screen/hidden */
.header-carousel-item .carousel-caption .animated {
  opacity: 0;
  transform: translateY(20px);
  /* Start slightly below final position */
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

/* 2. When the slide is active, animate the caption into view */
.header-carousel-item.active .carousel-caption .animated {
  opacity: 1;
  transform: translateY(0);
  /* Move to final position */
}

/* 3. Stagger the animation for title and subtitle */
/* Title: Starts immediately */
.header-carousel-item.active h4 {
  transition-delay: 0.2s;
}

/* Subtitle: Starts a little later */
.header-carousel-item.active p {
  transition-delay: 0.5s;
}

/* Social links (if they have the 'animated' class) */
.header-carousel-item.active .d-flex.align-items-center {
  transition-delay: 0.8s;
}

/* You must apply the 'animated' class to the elements you want to animate.
   In your template, the text block is already classed: */
/* <div class="col-xl-7 animated fadeInLeft"> */
/* We will target children of this block.
   Since you have an explicit 'animated fadeInLeft' on the column, 
   we can simplify the text animation by targeting its children: */

.header-carousel-item .col-xl-7.animated {
  /* For the whole text block to animate in */
  opacity: 0;
  transform: translateX(-50px);
  transition: opacity 1s ease-out, transform 1s ease-out;
}

.header-carousel-item.active .col-xl-7.animated {
  opacity: 1;
  transform: translateX(0);
  transition-delay: 0.3s;
}

/* Softer diagonal Kenya flag splash */
.header-carousel-item::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  pointer-events: none;

  /* Kenya Flag diagonal but transparent */
  background: linear-gradient(135deg,
      /* diagonal */
      rgba(0, 0, 0, 0.4) 0%,
      /* black (40% visible) */
      rgba(0, 0, 0, 0.4) 12%,

      rgba(255, 255, 255, 0.5) 12%,
      /* thin white */
      rgba(255, 255, 255, 0.5) 14%,

      rgba(193, 18, 31, 0.5) 14%,
      /* red */
      rgba(193, 18, 31, 0.5) 28%,

      rgba(255, 255, 255, 0.5) 28%,
      /* thin white */
      rgba(255, 255, 255, 0.5) 30%,

      rgba(0, 104, 71, 0.4) 30%,
      /* green */
      rgba(0, 104, 71, 0.4) 44%,

      transparent 44%,
      /* let image show through */
      transparent 100%);
}

 /* --- CORE CAPTION POSITIONING (Ensure it's absolute) --- */
.carousel-caption {
    position: absolute; /* Essential for top/bottom positioning */
    left: 0;
    right: 0;
    padding: 0 15px; /* Add some padding for the edges */
    z-index: 3;
    color: #fff;
    text-shadow: 2px 2px 6px rgba(0,0,0,0.9);
    
    /* Default: Transition on position properties */
    transition: top 1s ease-in-out, bottom 1s ease-in-out; 
}
</style>